// src/pages/RecordPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import LandscapeLayout from '../components/Layout/LandscapeLayout';
import PortraitLayout from '../components/Layout/PortraitLayout';
import useOrientation from '../hooks/useOrientation';
import PlayerControls from '../components/RecordPlayer/PlayerControls';
import PresentationPanel from '../components/RecordPlayer/PresentationPanel';
import FullScreenLoading from '../components/Loading/FullScreenLoading';
import { initRecordDataLoad } from '../components/RecordInit';
import CacheLoading from '../components/Loading/CacheLoading';
import LandscapeGuide from '../components/RecordPlayer/LandscapeGuide';


const RecordPage = () => {

  const [ searchParams ] = useSearchParams();

  const roomid = searchParams.get('roomid');
  const debugFixedMilliseconds = searchParams.get('debugFixedMilliseconds');

  const [isInitializing, setIsInitializing] = useState(true);
  const [loadingErrorMessage, setLoadingErrorMessage] = useState(null);
  const [initProgress, setInitProgress] = useState(0);

  const isLandscape = useOrientation();

  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1.0); // 默认1倍速
  const [isMediaReady, setIsMediaReady] = useState(true);
  const [isAudioReady, setIsAudioReady ] = useState(true);
  const [isVideoReady, setIsVideoReady ] = useState(true);
  const [duration, setDuration] = useState(5400); // 假设总时长为10分钟
  
  // 使用 ref 存储时间相关状态
  const isPlayingRef = useRef(isPlaying);
  const playbackRateRef = useRef(playbackRate);
  const startTimeRef = useRef(0); // 开始播放时的时间戳
  const accumulatedTimeRef = useRef(0); // 累计播放时间
  const animationRef = useRef(null); // 存储动画帧ID
  const lastUpdateRef = useRef(0); // 上次更新时间戳

  // 信令及课程、老师信息：sessionInfo, teacherInfo, recordInfo, presentationInfo, whiteboardInfo, chatInfo, audioInfo, videoInfo
  const [sessionInfoData, setSessionInfoData] = useState(null);
  const [teacherInfoData, setTeacherInfoData] = useState(null);
  const [presentationInfoData, setPresentationInfoData] = useState(null);
  const [presentationTimeNodes, setPresentationTimeNodes] = useState([]); // 新增：课件时间节点
  const [whiteboardInfoData, setWhiteboardInfoData] = useState(null);
  const [chatInfoData, setChatInfoData] = useState(null);
  const [audioInfoData, setAudioInfoData] = useState(null);
  const [videoInfoData, setVideoInfoData] = useState([]);
  const [cursorInfoData, setCursorInfoData] = useState(null);
  
  // 可用的播放速度
  const speedOptions = [1, 1.5, 1.75, 2];

  // 音频和视频的偏移时间
  const [audioOffset, setAudioOffset] = useState(0);
  const [videoOffset, setVideoOffset] = useState(0);
  
  useEffect(() => {

    let isCancelled = false; // 防止组件卸载后回调执行

    if (!roomid) {
      console.error('缺少 roomid');
      if (!isCancelled) setIsInitializing(false);
      return;
    }

    // 调用初始化函数
    initRecordDataLoad(roomid, ([result, progress, error]) => {

      if (isCancelled) return;

      if (error) { 
        setLoadingErrorMessage(error);
        return;
      }

      if (progress) { 
        setInitProgress(progress);
        return;
      }

      // 网页title更新
      document.title = `${result.sessionInfo.sessionName} - 录播回放 - 欧那教育`;

      // 赋值基础数据
      setDuration(result.recordInfo.length)
      setAudioOffset(result.audioInfo.milliseconds);

      // 增加视频矫正调试参数的实现逻辑
      if (result.videoInfo && result.videoInfo.length > 0 && debugFixedMilliseconds) {
        // 循环矫正视频开始时间
        result.videoInfo.forEach((video) => {
          if (!video.fixedMilliseconds) {
            video.milliseconds = video.milliseconds + debugFixedMilliseconds / 1000;
          }
        });
      }

      console.log('initRecordDataLoad result videoInfo:', result.videoInfo);

      setVideoOffset(result.videoInfo && result.videoInfo.length > 0 ? result.videoInfo[0].milliseconds: -1);
      setCurrentTime(result.audioInfo.milliseconds);

      accumulatedTimeRef.current = result.audioInfo.milliseconds;

      // 信令数据保存
      setSessionInfoData(result.sessionInfo);
      setTeacherInfoData(result.teacherInfo);
      setPresentationInfoData(result.presentationInfo);
      setPresentationTimeNodes(result.presentationTimeNodes); // 新增：设置课件时间节点
      setWhiteboardInfoData(result.whiteboardInfo);
      setChatInfoData(result.chatInfo);
      setAudioInfoData(result.audioInfo);
      setVideoInfoData(result.videoInfo);
      setCursorInfoData(result.cursorInfo);

      // ✅ 初始化完成，关闭 loading
      setIsInitializing(false);
    });

    // 清理动画帧
    return () => {
      isCancelled = true;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [roomid]);

  useEffect(() => { 
    // 延迟50毫秒，再执行，避免闪屏
    setTimeout(() => { 
      setIsMediaReady(isAudioReady && isVideoReady);
    }, 50);
  }, [isAudioReady, isVideoReady]);

  useEffect(() => { 
    if (!isMediaReady) return;

    accumulatedTimeRef.current = currentTime;

    if (isPlaying) {
      // 播放
      startTimeRef.current = performance.now();
      animationRef.current = requestAnimationFrame(updateTime);
    } else {
      // 暂停
      cancelAnimationFrame(animationRef.current);
    }

  }, [isMediaReady]);

  // 主时间轴更新 - 使用性能时间戳
  const updateTime = (timestamp) => {
    if (!startTimeRef.current) {
      startTimeRef.current = timestamp;
      lastUpdateRef.current = timestamp;
    }
  
    // console.log('updateTime', timestamp, startTimeRef.current, lastUpdateRef.current, accumulatedTimeRef.current);

    // 使用最新的 playbackRate 计算流逝时间
    const elapsed = (timestamp - startTimeRef.current) / 1000 * playbackRateRef.current;
    const newTime = accumulatedTimeRef.current + elapsed;
  
    // 限制时间不超过总时长
    const safeNewTime = Math.min(newTime, duration);

    setCurrentTime(safeNewTime);
  
    // 继续动画
    if (isPlayingRef.current && isMediaReady && safeNewTime < duration) {
      lastUpdateRef.current = timestamp;
      animationRef.current = requestAnimationFrame(updateTime);
    } else if (safeNewTime >= duration) {
      setIsPlaying(false);
      accumulatedTimeRef.current = duration;
      setCurrentTime(duration);
    } else {
      // 暂停时保存累计时间
      accumulatedTimeRef.current = safeNewTime;
    }
  };
  
  // 处理播放/暂停
  const handlePlayPause = () => {
    
    // 重置累计时间为当前的播放时间
    accumulatedTimeRef.current = currentTime;
    // 播放
    startTimeRef.current = performance.now();

    if (isPlaying) {
      // 暂停
      cancelAnimationFrame(animationRef.current);
    } else {
      animationRef.current = requestAnimationFrame(updateTime);
    }
    setIsPlaying(!isPlaying);
  };

  // 处理进度跳转
  const handleSeek = (time) => {

    // 确保时间在有效范围内
    const newTime = Math.max(0, Math.min(time, duration));

    setCurrentTime(newTime);
    accumulatedTimeRef.current = newTime;
    
    // 如果正在播放，需要更新开始时间
    if (isPlaying) {
      cancelAnimationFrame(animationRef.current);
      startTimeRef.current = performance.now();
      animationRef.current = requestAnimationFrame(updateTime);
    }
  };
  
  // 后退5秒
  const handleBackward = () => {
    handleSeek(currentTime - 15);
  };
  
  // 前进5秒
  const handleForward = () => {

    handleSeek(currentTime + 15);
  };

  const onVideoStateChange = (state) => { 
    setIsVideoReady(state);
  };

  const onAudioStateChange = (state) => { 
    setIsAudioReady(state);
  };

  // 倍速切换函数
  const changePlaybackRate = (rate) => {

    const now = performance.now();
    
    setPlaybackRate(rate);
    
    // 如果正在播放，重置 startTimeRef，使新倍速立即生效
    if (isPlaying) {
      cancelAnimationFrame(animationRef.current);
      startTimeRef.current = now; // 重置开始时间为当前时间
      lastUpdateRef.current = now;
      accumulatedTimeRef.current = currentTime;
      animationRef.current = requestAnimationFrame(updateTime);
    }
  };
  
  // 清理动画帧
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  useEffect(() => { 
    playbackRateRef.current = playbackRate;
  }, [playbackRate]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);
  
  // 🔁 条件渲染：初始化中显示 loading，完成后渲染主界面
  if (isInitializing) {
    return <FullScreenLoading isMobile={!isLandscape} progress={initProgress} loadingErrorMessage={loadingErrorMessage} />;
  }

  return (
    <div className={`record-container ${isLandscape ? 'landscape' : 'portrait'}`}>
      <div className="header-message">
        {/* <span id="audio-play-offset" style={{ marginLeft: '10px', marginRight: '10px'}}></span> */}
        {/* <div id="video-logs-text" style={{ marginLeft: '10px', marginRight: '10px', maxHeight: '200px', overflow: 'auto'}}></div> */}
        {/* <div id="cache-logs-text" style={{ marginLeft: '10px', marginRight: '10px', maxHeight: '300px', overflow: 'auto'}}></div> */}
        {/* <span id="audio-logs-text" style={{ marginLeft: '10px', marginRight: '10px'}}></span>
        <span id="video-logs-text" style={{ marginLeft: '10px', marginRight: '10px'}}></span> */}
      </div>
      <div className="content-area">
        {isLandscape ? (
          <LandscapeLayout 
            roomid={roomid}
            currentTime={currentTime}
            isPlaying={isPlaying}
            playbackRate={playbackRate}
            sessionInfo={sessionInfoData}
            teacherInfo={teacherInfoData}
            presentationInfo={presentationInfoData}
            whiteboardInfo={whiteboardInfoData}
            cursorInfo={cursorInfoData}
            chatInfo={chatInfoData}
            videoInfo={videoInfoData}
            isMediaReady={isMediaReady}
            onVideoStateChange={onVideoStateChange}
          />
        ) : (
          <PortraitLayout 
            roomid={roomid}
            currentTime={currentTime}
            isPlaying={isPlaying}
            playbackRate={playbackRate}
            sessionInfo={sessionInfoData}
            teacherInfo={teacherInfoData}
            presentationInfo={presentationInfoData}
            whiteboardInfo={whiteboardInfoData}
            cursorInfo={cursorInfoData}
            chatInfo={chatInfoData}
            videoInfo={videoInfoData}
            isMediaReady={isMediaReady}
            onVideoStateChange={onVideoStateChange}
          />
        )}
      </div>

      {/* <div className="control-bar">  */}
      <PlayerControls
        roomid={roomid}
        currentTime={currentTime}
        sessionInfo={sessionInfoData}
        audioInfo={audioInfoData}
        duration={duration}
        isMobile={!isLandscape}
        onSeek={handleSeek}
        playbackRate={playbackRate}
        isPlaying={isPlaying}
        onPlayPause={handlePlayPause}
        onBackward={handleBackward}
        onForward={handleForward}
        onChangePlaybackRate={changePlaybackRate}
        speedOptions={speedOptions}
        audioOffset={audioOffset}
        videoOffset={videoOffset}
        isMediaReady={isMediaReady}
        onAudioStateChange={onAudioStateChange}
        presentationTimeNodes={presentationTimeNodes} // 新增：传递课件时间节点
      />

      <PresentationPanel
        presentationTimeNodes={presentationTimeNodes}
        currentTime={currentTime}
        duration={duration}
        onSeek={handleSeek}
        isMobile={!isLandscape}
      />

      <CacheLoading isLoading={!isMediaReady} text={'加载中，请稍等......'} />

      <LandscapeGuide isMobile={!isLandscape} />
    </div>
  );
};

export default RecordPage;